class SearchableSelect extends HTMLElement {
    $search = document.createElement('searchable-select-container')
    $input = document.createElement('INPUT')
    $select = null
    $results = document.createElement('DIV')
	$reset = document.createElement('BUTTON')
    #optionsObserver = null
    #childObserver = null
    #domBuilt = false
    #eventsBound = false
    #selectValuePatched = false
    #documentClickHandler = null
    #lastSelectValue = null

    constructor() {
        super()
    }

    connectedCallback() {
        this.#boot()
    }

    disconnectedCallback() {
        this.#optionsObserver?.disconnect()
        this.#childObserver?.disconnect()
        this.#optionsObserver = null
        this.#childObserver = null

        if (this.#documentClickHandler) {
            document.removeEventListener('click', this.#documentClickHandler)
            this.#documentClickHandler = null
        }
    }

    static get observedAttributes() {
        return ['placeholder', 'aria-label']
    }

    attributeChangedCallback(name, _, newValue) {
        if (!this.$input) {
            return
        }

        if (newValue === null) {
            this.$input.removeAttribute(name)
        } else {
            this.$input.setAttribute(name, newValue)
        }
    }

    #boot() {
        this.$select = this.querySelector('select')

        if (!this.$select) {
            if (!this.#childObserver) {
                this.#childObserver = new MutationObserver(() => this.#boot())
                this.#childObserver.observe(this, { childList: true })
            }
            return
        }

        this.#childObserver?.disconnect()
        this.#childObserver = null

        const $existingInput = this.querySelector('input')
        if ($existingInput) {
            this.$input = $existingInput
        }

        this.#patchSelectValue()
        this.#observeSelect()
        this.render()
        this.syncSelectFromMarkup()
        this.ensurePlaceholderOption()
        this.syncFromSelect(true)
    }

    #patchSelectValue() {
        if (this.#selectValuePatched || !this.$select) {
            return
        }

        const descriptor = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')
        const originalGet = descriptor.get
        const originalSet = descriptor.set
        const self = this

        Object.defineProperty(this.$select, 'value', {
            configurable: true,
            enumerable: descriptor.enumerable,
            get() {
                return originalGet.call(this)
            },
            set(value) {
                originalSet.call(this, value)
                self.syncFromSelect()
            },
        })

        this.#selectValuePatched = true
    }

    #observeSelect() {
        this.#optionsObserver?.disconnect()

        const observer = new MutationObserver(() => {
            this.syncSelectFromMarkup()
            this.syncFromSelect(true)
        })

        observer.observe(this.$select, {
            childList: true,
            attributes: true,
            subtree: true,
            attributeFilter: ['selected', 'value'],
        })

        this.#optionsObserver = observer
    }

    render() {
        this.buildSearch()
        this.bootEvents()
    }

    buildSearch() {
        if (this.#domBuilt) {
            return
        }

		this.$input.type = 'text'

        this.$reset.type = 'button'
        this.$reset.classList.add('reset-btn')
        this.$reset.setAttribute('aria-label', 'Reset')
        this.$reset.hidden = true
        
        this.$search.appendChild(this.$input)
		this.$search.appendChild(this.$reset)

        this.$results.classList.add('results')
        this.$results.setAttribute('hidden', '')
        this.$search.appendChild(this.$results)
        this.prepend(this.$search)
        this.#domBuilt = true
    }

    getSelectedValue() {
        if (!this.$select) {
            return ''
        }

        return this.$select.value
    }

    getSelectedOption() {
        if (!this.$select) {
            return null
        }

        const value = this.getSelectedValue()

        if (value === '') {
            return this.$select.querySelector('option[value=""]') || null
        }

        return this.$select.querySelector(`option[value="${CSS.escape(value)}"]`)
    }

    getUserSelected() {
        const $option = this.getSelectedOption()

        if (!$option) {
            return null
        }

        return {
            value: $option.value,
            text: $option.textContent,
        }
    }

    syncSelectFromMarkup() {
        if (!this.$select || this.$select.value !== '') {
            return
        }

        const $selected = this.$select.querySelector('option[selected]')

        if ($selected) {
            this.$select.value = $selected.value
        }
    }

    ensurePlaceholderOption() {
        if (!this.$select || this.$select.querySelector('option[value=""]')) {
            return
        }

        const $blank = document.createElement('option')
        $blank.value = ''
        this.$select.insertBefore($blank, this.$select.firstChild)
    }

    syncFromSelect(force = false) {
        if (!this.$select) {
            return
        }

        const currentValue = this.getSelectedValue()
        const selected = this.getUserSelected()
        const expectedText = selected ? selected.text : ''

        if (!force && currentValue === this.#lastSelectValue) {
            if (this.$input.value === expectedText) {
                return
            }

            if (this.$input.value !== '' && this.$input.value !== expectedText) {
                return
            }
        }

        this.#lastSelectValue = currentValue
        this.$input.value = expectedText
        this.renderResults()
        this.syncResetButton()
    }

    hasSelectedValue() {
        return this.getSelectedValue() !== ''
    }

    syncResetButton() {
        this.$reset.hidden = !this.hasSelectedValue()
    }

    select($to_select) {
        if (!$to_select?.classList?.contains('result')) {
            return
        }

        const value = $to_select.dataset.value ?? ''

        if (!this.$select) {
            return
        }

        if (this.$select.value === value) {
            this.$input.value = $to_select.textContent
            this.$results.setAttribute('hidden', '')
            return
        }

        this.$select.value = value
        this.$input.value = $to_select.textContent
        this.$results.setAttribute('hidden', '')
        this.renderResults()

        this.$select.dispatchEvent(new Event('change', { bubbles: true }))
        this.dispatchEvent(new Event('change', { bubbles: true }))
    }


	reset() {
		if (!this.$select || !this.hasSelectedValue()) {
			return
		}

		this.$select.value = ''
		this.$results.setAttribute('hidden', '')

		this.$select.dispatchEvent(new Event('change', { bubbles: true }))
		this.dispatchEvent(new Event('change', { bubbles: true }))
	}

    bootEvents() {
        if (this.#eventsBound) {
            return
        }

        this.$search.addEventListener('click', () => {
            this.$results.toggleAttribute('hidden')
			//this.$results.querySelector('[data-selected]')?.scrollIntoView()
        })

        this.$results.addEventListener('click', (event) => {
            this.select(event.target)
        })

        const handleKeyboard = this.handleKeyoard.bind(this)

        this.$input.addEventListener('input', ({ target: { value } }) => {
            this.renderResults(value)
        })

		this.$reset.addEventListener('click', (event) => {
			event.stopPropagation()
			this.reset()
		})

        this.$input.addEventListener('focus', () => {
            this.$input.addEventListener('keydown', handleKeyboard)
        })

        this.$input.addEventListener('blur', () => {
            this.$input.removeEventListener('keydown', handleKeyboard)
        })

        this.#documentClickHandler = (event) => {
            if (!this.contains(event.target) && !this.$results.hasAttribute('hidden')) {
                this.$results.setAttribute('hidden', '')
            }
        }

        document.addEventListener('click', this.#documentClickHandler)

        this.#eventsBound = true
    }

    renderResults(search = '') {
        if (!this.$select) {
            return
        }

        const selectedValue = this.getSelectedValue()
        const $options = Array.from(this.$select.querySelectorAll('option'))
        const $result_list = []
        let has_selected = false
        let set_default_hover = false

        for (const $o of $options) {
            if (search !== '') {
                const is_ok = $o.textContent.toLowerCase().includes(search.trim().toLowerCase())
                if (!is_ok) {
                    continue
                }
            }

            if ($o.value === '') {
                if ($o.value === selectedValue) {
                    set_default_hover = true
                }
                continue
            }

            const $div = document.createElement('DIV')
            $div.textContent = $o.textContent
            $div.dataset.value = $o.value
            $div.classList.add('result')

            if ($o.value === selectedValue) {
                $div.setAttribute('data-selected', '')
                $div.setAttribute('data-hover', '')
                has_selected = true
            }

            $result_list.push($div)
        }

        if (set_default_hover || !has_selected) {
            $result_list[0]?.setAttribute('data-hover', '')
        }

        this.$results.replaceChildren(...$result_list)
    }

    updateHover($element) {
        const $prev = this.$results.querySelector('[data-hover]')
        $prev?.removeAttribute('data-hover')
        $element?.setAttribute('data-hover', '')
    }

    handleKeyoard(event) {
        const $current_hover = this.$results.querySelector('[data-hover]')

        if (!$current_hover) {
            return
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                const $next = $current_hover.nextElementSibling
                if (!$next) {
                    return
                }
                this.updateHover($next)
                $next.scrollIntoView({ block: 'nearest' })
                break
            case 'ArrowUp':
                event.preventDefault()
                const $prev = $current_hover.previousElementSibling
                if (!$prev) {
                    return
                }
                this.updateHover($prev)
                $prev.scrollIntoView({ block: 'nearest' })
                break
            case 'Enter':
                event.preventDefault()
                $current_hover.click()
                break
        }
    }
}

window.customElements.define('uix-searchable-select', SearchableSelect)
